import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, inr, inrShort, requestErrorMessage } from "@/lib/erp";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import {
  TrendingUp, Wallet, AlertTriangle, IndianRupee, Users, Package,
  FileText, ArrowUpRight, PackageX, CalendarClock,
} from "lucide-react";

const StatCard = ({ title, value, icon: Icon, tone, sub, testid }) => (
  <Card className="p-5 border-slate-200/80" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</div>
        <div className="font-heading text-2xl font-bold mt-2 tabular">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </div>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await http.get("/dashboard/stats");
      if (!response.data) {
        setError("Dashboard data was empty. Please retry.");
        return;
      }
      setD(response.data);
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "load the dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const writeOff = async (e) => {
    if (!e.stock || e.stock <= 0) return toast.info("No stock to write off");
    if (!window.confirm(`Write off ${e.stock} of ${e.name} (batch ${e.batch_no}) as expired?`)) return;
    try {
      await http.post("/inventory/adjust", { product_id: e.id, qty_delta: -e.stock, reason: "Expired" });
      toast.success(`Wrote off ${e.name}`);
      await load();
    } catch (requestError) {
      toast.error(requestErrorMessage(requestError, "write off this stock"));
    }
  };

  if (loading && !d) return <div className="p-8 text-muted-foreground">Loading dashboard…</div>;
  if (error && !d) return (
    <div className="p-8 space-y-3" role="alert">
      <p className="text-rose-700">{error}</p>
      <button onClick={load} className="text-sm text-primary underline">Retry</button>
    </div>
  );
  if (!d) return null;

  const monthName = new Date().toLocaleDateString("en-IN", { month: "short" });
  const trend = (d.sales_trend || []).map((t) => ({
    ...t, m: new Date(t.month + "-01").toLocaleDateString("en-IN", { month: "short" }),
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Business overview for VGRUPS Health Care</p>
        </div>
        <button onClick={() => navigate("/sales/invoices/new")}
          data-testid="dashboard-create-invoice"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">
          <FileText className="h-4 w-4" /> Create Invoice
        </button>
      </div>
      {error && <div className="flex items-center gap-3 text-sm text-rose-700" role="alert"><span>{error}</span><button onClick={load} className="underline">Retry</button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {can("view_financials") ? (<>
        <StatCard title="Total Sales" value={inrShort(d.total_sales)} icon={IndianRupee}
          tone="bg-brand-50 text-brand-700" sub={`${d.invoice_count} invoices`} testid="stat-total-sales" />
        <StatCard title="Collection" value={inrShort(d.collection)} icon={Wallet}
          tone="bg-emerald-50 text-emerald-700" sub="Received to date" testid="stat-collection" />
        <StatCard title="Outstanding" value={inrShort(d.outstanding)} icon={AlertTriangle}
          tone="bg-amber-50 text-amber-700" sub={`${d.overdue_count} overdue`} testid="stat-outstanding" />
        <StatCard title={`${monthName} Sales`} value={inrShort(d.month_sales)} icon={TrendingUp}
          tone="bg-brand-50 text-brand-700" sub="This month" testid="stat-month-sales" />
        <StatCard title="Purchase Due" value={inrShort(d.purchase_outstanding)} icon={Wallet}
          tone="bg-rose-50 text-rose-700" sub={`of ${inrShort(d.purchase_total)} purchased`} testid="stat-purchase-due" />
        </>) : (<>
        <StatCard title="Invoices" value={d.invoice_count} icon={FileText} tone="bg-brand-50 text-brand-700" sub="Total documents" testid="stat-invoice-count" />
        <StatCard title="Customers" value={d.customer_count} icon={Users} tone="bg-emerald-50 text-emerald-700" sub="Active parties" testid="stat-customer-count" />
        <StatCard title="Products" value={d.product_count} icon={Package} tone="bg-brand-50 text-brand-700" sub="SKUs" testid="stat-product-count" />
        <StatCard title="Low Stock" value={d.low_stock.length} icon={PackageX} tone="bg-rose-50 text-rose-700" sub="Need reorder" testid="stat-low-count" />
        </>)}
      </div>

      {can("view_financials") && (
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-heading font-semibold mb-4">Sales Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#97d0d6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#97d0d6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="m" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => inrShort(v)} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={60} />
              <Tooltip formatter={(v) => inr(v)} />
              <Area type="monotone" dataKey="sales" stroke="#3F8F99" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading font-semibold mb-4">Top Customers</h3>
          <div className="space-y-3">
            {d.top_customers.length === 0 && <div className="text-sm text-muted-foreground">No data</div>}
            {d.top_customers.map((c, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`top-customer-${i}`}>
                <div className="h-8 w-8 rounded-full bg-slate-100 grid place-items-center text-xs font-semibold text-slate-600">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                </div>
                <div className="text-sm font-mono font-semibold">{inrShort(c.amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-rose-600">
            <PackageX className="h-4 w-4" /><h3 className="font-heading font-semibold text-foreground">Low Stock</h3>
          </div>
          <div className="space-y-2">
            {d.low_stock.length === 0 && <div className="text-sm text-muted-foreground">All stocked up</div>}
            {d.low_stock.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm" data-testid={`low-stock-${p.id}`}>
                <span className="truncate">{p.name}</span>
                <span className="font-mono text-rose-600">{p.stock}/{p.reorder_level}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-amber-600">
            <CalendarClock className="h-4 w-4" /><h3 className="font-heading font-semibold text-foreground">Expiring Soon</h3>
          </div>
          <div className="space-y-2">
            {d.expiring.length === 0 && <div className="text-sm text-muted-foreground">No near-expiry batches</div>}
            {d.expiring.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm gap-2" data-testid={`expiring-${i}`}>
                <span className="truncate flex-1">{e.name} <span className="text-xs text-muted-foreground">({e.batch_no})</span></span>
                <span className={`font-mono ${e.days < 0 ? "text-rose-600" : "text-amber-600"}`}>{e.expiry_date}</span>
                {can("manage_inventory") && (
                  <button onClick={() => writeOff(e)} data-testid={`writeoff-${i}`}
                    className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200">
                    Write off
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-rose-600">
            <AlertTriangle className="h-4 w-4" /><h3 className="font-heading font-semibold text-foreground">Overdue Invoices</h3>
          </div>
          <div className="space-y-2">
            {d.overdue_invoices.length === 0 && <div className="text-sm text-muted-foreground">Nothing overdue</div>}
            {d.overdue_invoices.map((o) => (
              <div key={o.id} onClick={() => navigate(`/sales/invoices/${o.id}`)}
                className="flex items-center justify-between text-sm cursor-pointer hover:text-primary" data-testid={`overdue-${o.id}`}>
                <span className="truncate">{o.invoice_no} · {o.customer_name}</span>
                <span className="font-mono text-rose-600">{inrShort(o.balance_due)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold">Recent Invoices</h3>
          <button onClick={() => navigate("/sales/invoices")} className="text-sm text-primary flex items-center gap-1 hover:underline">
            View all <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                <th className="py-2 font-medium">Invoice</th><th className="font-medium">Customer</th>
                <th className="font-medium">Date</th><th className="font-medium text-right">Amount</th>
                <th className="font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.recent_invoices.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/sales/invoices/${r.id}`)}
                  className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`recent-inv-${r.id}`}>
                  <td className="py-2.5 font-mono font-medium">{r.invoice_no}</td>
                  <td>{r.customer_name}</td>
                  <td className="text-muted-foreground">{r.invoice_date}</td>
                  <td className="text-right font-mono">{inr(r.grand_total)}</td>
                  <td className="text-right"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
